import clsx from "clsx";
import { CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import React, { useEffect } from "react";
import IntlTelInputBase from "react-intl-tel-input";

export interface IntlTelData {
  phone: string;
  phoneCountryCode: string;
  phonePrefix: string;
  isPhoneValid: boolean;
  countryName?: string;
}

interface Props {
  phone: string;
  phoneCountryCode: string;
  onChange?: (data: IntlTelData) => void;
  focusBorderColor?: string;
  disabled?: boolean;
  fieldName?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}

export const IntlTelInput: React.FC<Props> = ({
  phone,
  phoneCountryCode,
  onChange,
  focusBorderColor = "focus:border-bgDarkGray",
  fieldName = "",
  disabled,
  onBlur,
}) => {
  const inputClasses = [
    "font-normal",
    "border",
    "border-solid",
    "border-bgDarkGray",
    "py-2",
    "leading-tight",
    "px-3.5",
    "outline-0",
    "focus-visible:ring-0",
    focusBorderColor,
  ];

  const checkNumberValidity = (number: unknown, iso2: string) => {
    if (typeof number !== "string" && typeof number !== "number") return false;

    const numStr = String(number);

    if (!numStr || typeof numStr.charAt !== "function") return false;

    return (
      iso2 &&
      isValidPhoneNumber(numStr, iso2.toUpperCase() as CountryCode) &&
      numStr.charAt(0) !== "0"
    );
  };

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;

    const interval = setInterval(() => {
      const input = document.querySelector(
        ".intl-tel-input input"
      ) as HTMLInputElement | null;

      if (input && input.placeholder !== "Enter phone number") {
        input.placeholder = "Enter phone number";
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="intl-tel-wrapper"
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          onBlur?.();
        }
      }}
    >
      <IntlTelInputBase
        disabled={disabled}
        inputClassName={clsx(
          ...inputClasses,
          "pl-[100px] w-full",
          disabled
            ? "text-gray-400 cursor-not-allowed"
            : "border text-sm   bg-[#141414] placeholder:text-textDarkGrayColor rounded-xl border-bgDarkGray  py-4  text-white  customInputBlack makeBgBlack"
        )}
        fieldName={fieldName}
        preferredCountries={["in", "us", "ae"]}
        value={phone ?? ""}
        separateDialCode={true}
        defaultCountry={phoneCountryCode?.toLowerCase() || "in"}
        // autoPlaceholder={false}
        //  inputProps={{ placeholder: 'Enter phone number' }}
        formatOnInit={false}
        format={false}
        onSelectFlag={(number, { iso2, dialCode, name }, _, isValid) => {
          const valid = isValid ?? checkNumberValidity(number, iso2 as string);
          onChange &&
            onChange({
              phone: String(number),
              phoneCountryCode: iso2 as string,
              phonePrefix: dialCode as string,
              isPhoneValid: valid,
              countryName: name,
            });
        }}
        onPhoneNumberChange={(isValid, value, { dialCode, iso2, name }) => {
          const valid = isValid ?? checkNumberValidity(value, iso2 as string);
          onChange &&
            onChange({
              phone: String(value),
              phoneCountryCode: iso2 as string,
              phonePrefix: dialCode as string,
              isPhoneValid: valid,
              countryName: name,
            });
        }}
      />
    </div>
  );
};
